import {
    getFirestore,
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
    serverTimestamp,
    Timestamp,
    writeBatch
} from 'firebase/firestore';
import { db } from '../services/firebaseService';

export interface Friend {
    id: string;
    username: string;
    level: number;
    rank: string;
    tier: 'free' | 'premium' | 'max';
    isOnline: boolean;
    lastSeen?: number;
    questsCompleted: number;
    streak: number;
    addedAt?: number;
}

export interface FriendRequest {
    id: string;
    fromUser: {
        id: string;
        username: string;
        level: number;
        rank: string;
        tier: 'free' | 'premium' | 'max';
    };
    toUserId: string;
    status: 'pending' | 'accepted' | 'declined';
    timestamp: number;
}

export interface UserSearchResult {
    id: string;
    username: string;
    level: number;
    rank: string;
    tier: 'free' | 'premium' | 'max';
    questsCompleted: number;
}

/**
 * Send a friend request to another user
 */
export async function sendFriendRequest(
    fromUserId: string,
    toUserId: string,
    fromUserData: { username: string; level: number; rank: string; tier: 'free' | 'premium' | 'max' }
): Promise<{ success: boolean; message?: string }> {
    try {
        if (fromUserId === toUserId) {
            return { success: false, message: "You can't send a friend request to yourself" };
        }

        // Check if already friends
        const friendDoc = await getDoc(doc(db, `users/${fromUserId}/friends/${toUserId}`));
        if (friendDoc.exists()) {
            return { success: false, message: 'Already friends' };
        }

        // Check if request already exists
        const requestsQuery = query(
            collection(db, 'friendRequests'),
            where('fromUserId', '==', fromUserId),
            where('toUserId', '==', toUserId),
            where('status', '==', 'pending')
        );
        const existingRequests = await getDocs(requestsQuery);

        if (!existingRequests.empty) {
            return { success: false, message: 'Friend request already sent' };
        }

        // Create friend request
        const requestId = `${fromUserId}_${toUserId}_${Date.now()}`;
        await setDoc(doc(db, 'friendRequests', requestId), {
            fromUserId,
            toUserId,
            fromUser: fromUserData,
            status: 'pending',
            timestamp: Date.now(),
            createdAt: serverTimestamp()
        });

        return { success: true, message: 'Friend request sent!' };
    } catch (error) {
        console.error('[SocialService] Send friend request error:', error);
        return { success: false, message: 'Failed to send friend request' };
    }
}

/**
 * Accept a friend request
 */
export async function acceptFriendRequest(requestId: string): Promise<{ success: boolean; message?: string }> {
    try {
        const requestDoc = await getDoc(doc(db, 'friendRequests', requestId));

        if (!requestDoc.exists()) {
            return { success: false, message: 'Friend request not found' };
        }

        const requestData = requestDoc.data();
        const { fromUserId, toUserId, fromUser } = requestData;

        // Get recipient user data
        const toUserDoc = await getDoc(doc(db, 'users', toUserId));
        if (!toUserDoc.exists()) {
            return { success: false, message: 'User not found' };
        }

        const toUserData = toUserDoc.data();
        const batch = writeBatch(db);

        // Add to both users' friends collections
        batch.set(doc(db, `users/${fromUserId}/friends/${toUserId}`), {
            userId: toUserId,
            username: toUserData.username,
            level: toUserData.level || 1,
            rank: toUserData.rank || 'Beginner',
            tier: toUserData.accountTier || 'free',
            addedAt: Date.now(),
            createdAt: serverTimestamp()
        });

        batch.set(doc(db, `users/${toUserId}/friends/${fromUserId}`), {
            userId: fromUserId,
            username: fromUser.username,
            level: fromUser.level,
            rank: fromUser.rank,
            tier: fromUser.tier || 'free',
            addedAt: Date.now(),
            createdAt: serverTimestamp()
        });

        // Update request status
        batch.update(doc(db, 'friendRequests', requestId), {
            status: 'accepted',
            acceptedAt: serverTimestamp()
        });

        await batch.commit();

        return { success: true, message: 'Friend request accepted!' };
    } catch (error) {
        console.error('[SocialService] Accept friend request error:', error);
        return { success: false, message: 'Failed to accept friend request' };
    }
}

/**
 * Decline a friend request
 */
export async function declineFriendRequest(requestId: string): Promise<{ success: boolean }> {
    try {
        await updateDoc(doc(db, 'friendRequests', requestId), {
            status: 'declined',
            declinedAt: serverTimestamp()
        });
        return { success: true };
    } catch (error) {
        console.error('[SocialService] Decline friend request error:', error);
        return { success: false };
    }
}

/**
 * Remove a friend
 */
export async function removeFriend(userId: string, friendId: string): Promise<{ success: boolean }> {
    try {
        const batch = writeBatch(db);

        batch.delete(doc(db, `users/${userId}/friends/${friendId}`));
        batch.delete(doc(db, `users/${friendId}/friends/${userId}`));

        await batch.commit();
        return { success: true };
    } catch (error) {
        console.error('[SocialService] Remove friend error:', error);
        return { success: false };
    }
}

/**
 * Get user's friends list
 */
export async function getFriends(userId: string): Promise<Friend[]> {
    try {
        const friendsSnapshot = await getDocs(collection(db, `users/${userId}/friends`));
        const friends: Friend[] = [];

        for (const friendDoc of friendsSnapshot.docs) {
            const friendData = friendDoc.data();
            const friendUserId = friendDoc.id;

            // Get real-time user data
            const userDoc = await getDoc(doc(db, 'users', friendUserId));
            if (userDoc.exists()) {
                const userData = userDoc.data();
                friends.push({
                    id: friendUserId,
                    username: userData.username || friendData.username,
                    level: userData.level || 1,
                    rank: userData.rank || 'Beginner',
                    tier: userData.accountTier || 'free',
                    isOnline: userData.isOnline || false,
                    lastSeen: userData.lastSeen || Date.now(),
                    questsCompleted: userData.questsCompleted || 0,
                    streak: userData.streakCount || 0,
                    addedAt: friendData.addedAt
                });
            }
        }

        return friends.sort((a, b) => {
            // Sort by online status first, then by addedAt
            if (a.isOnline && !b.isOnline) return -1;
            if (!a.isOnline && b.isOnline) return 1;
            return (b.addedAt || 0) - (a.addedAt || 0);
        });
    } catch (error) {
        console.error('[SocialService] Get friends error:', error);
        return [];
    }
}

/**
 * Get pending friend requests for a user
 */
export async function getPendingRequests(userId: string): Promise<FriendRequest[]> {
    try {
        const requestsQuery = query(
            collection(db, 'friendRequests'),
            where('toUserId', '==', userId),
            where('status', '==', 'pending'),
            orderBy('timestamp', 'desc')
        );

        const requestsSnapshot = await getDocs(requestsQuery);
        const requests: FriendRequest[] = [];

        requestsSnapshot.forEach(doc => {
            const data = doc.data();
            requests.push({
                id: doc.id,
                fromUser: data.fromUser,
                toUserId: data.toUserId,
                status: data.status,
                timestamp: data.timestamp
            });
        });

        return requests;
    } catch (error) {
        console.error('[SocialService] Get pending requests error:', error);
        return [];
    }
}

/**
 * Search users by username
 */
export async function searchUsers(searchQuery: string, currentUserId: string): Promise<UserSearchResult[]> {
    try {
        if (!searchQuery.trim()) return [];

        const usersQuery = query(
            collection(db, 'users'),
            where('username', '>=', searchQuery),
            where('username', '<=', searchQuery + '\uf8ff'),
            limit(20)
        );

        const usersSnapshot = await getDocs(usersQuery);
        const results: UserSearchResult[] = [];

        // Get current user's friends to filter them out
        const friendsSnapshot = await getDocs(collection(db, `users/${currentUserId}/friends`));
        const friendIds = new Set(friendsSnapshot.docs.map(doc => doc.id));

        usersSnapshot.forEach(doc => {
            if (doc.id !== currentUserId && !friendIds.has(doc.id)) {
                const data = doc.data();
                results.push({
                    id: doc.id,
                    username: data.username || 'Unknown',
                    level: data.level || 1,
                    rank: data.rank || 'Beginner',
                    tier: data.accountTier || 'free',
                    questsCompleted: data.questsCompleted || 0
                });
            }
        });

        return results;
    } catch (error) {
        console.error('[SocialService] Search users error:', error);
        return [];
    }
}

/**
 * Update user's online status
 */
export async function updateOnlineStatus(userId: string, isOnline: boolean): Promise<void> {
    try {
        await updateDoc(doc(db, 'users', userId), {
            isOnline,
            lastSeen: Date.now(),
            lastSeenAt: serverTimestamp()
        });
    } catch (error) {
        console.error('[SocialService] Update online status error:', error);
    }
}

/**
 * Listen to friend status changes in real-time
 */
export function onFriendsChange(userId: string, callback: (friends: Friend[]) => void): () => void {
    const unsubscribe = onSnapshot(
        collection(db, `users/${userId}/friends`),
        async (snapshot) => {
            const friends = await getFriends(userId);
            callback(friends);
        },
        (error) => {
            console.error('[SocialService] Friends listener error:', error);
        }
    );

    return unsubscribe;
}

/**
 * Listen to new friend requests in real-time
 */
export function onFriendRequestsChange(userId: string, callback: (requests: FriendRequest[]) => void): () => void {
    const requestsQuery = query(
        collection(db, 'friendRequests'),
        where('toUserId', '==', userId),
        where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(
        requestsQuery,
        (snapshot) => {
            const requests: FriendRequest[] = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                requests.push({
                    id: doc.id,
                    fromUser: data.fromUser,
                    toUserId: data.toUserId,
                    status: data.status,
                    timestamp: data.timestamp
                });
            });
            callback(requests.sort((a, b) => b.timestamp - a.timestamp));
        },
        (error) => {
            console.error('[SocialService] Friend requests listener error:', error);
        }
    );

    return unsubscribe;
}

/**
 * Get online friends only
 */
export async function getOnlineFriends(userId: string): Promise<Friend[]> {
    try {
        const allFriends = await getFriends(userId);
        return allFriends.filter(friend => friend.isOnline);
    } catch (error) {
        console.error('[SocialService] Get online friends error:', error);
        return [];
    }
}

/**
 * Get Weekly Top Students Leaderboard
 */
export async function getWeeklyTopStudents(limitCount: number = 10): Promise<(UserSearchResult & { weeklyXP: number })[]> {
    try {
        const usersQuery = query(
            collection(db, 'users'),
            orderBy('weeklyXP', 'desc'), // requires index
            limit(limitCount)
        );

        const snapshot = await getDocs(usersQuery);
        const results: (UserSearchResult & { weeklyXP: number })[] = [];

        snapshot.forEach(doc => {
            const data = doc.data();
            if (!data.isAnonymous && data.username) {
                results.push({
                    id: doc.id,
                    username: data.username,
                    level: data.level || 1,
                    rank: data.rank || 'Beginner',
                    tier: data.accountTier || 'free',
                    questsCompleted: data.questsCompleted || 0,
                    weeklyXP: data.weeklyXP || 0
                });
            }
        });

        return results;
    } catch (error) {
        console.error('[SocialService] Get leaderboard error:', error);
        return [];
    }
}

// ==================== DIRECT MESSAGING ====================

export interface DirectMessage {
    id: string;
    senderId: string;
    receiverId: string;
    content: string;
    timestamp: number;
    read: boolean;
}

export interface Conversation {
    id: string;
    participantIds: string[];
    lastMessage: string;
    lastMessageAt: number;
    unreadCount: number;
    friend: Friend;
}

/**
 * Get or create a conversation between two users
 */
export async function getOrCreateConversation(userId1: string, userId2: string): Promise<string> {
    try {
        // Conversation ID is deterministic based on sorted user IDs
        const sortedIds = [userId1, userId2].sort();
        const conversationId = `dm_${sortedIds[0]}_${sortedIds[1]}`;

        const convDoc = await getDoc(doc(db, 'directMessages', conversationId));

        if (!convDoc.exists()) {
            await setDoc(doc(db, 'directMessages', conversationId), {
                participantIds: sortedIds,
                createdAt: serverTimestamp(),
                lastMessageAt: Date.now(),
                lastMessage: ''
            });
        }

        return conversationId;
    } catch (error) {
        console.error('[SocialService] Get/create conversation error:', error);
        throw error;
    }
}

/**
 * Send a direct message
 */
export async function sendDirectMessage(
    conversationId: string,
    senderId: string,
    receiverId: string,
    content: string
): Promise<{ success: boolean; message?: DirectMessage }> {
    try {
        const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const message: DirectMessage = {
            id: messageId,
            senderId,
            receiverId,
            content,
            timestamp: Date.now(),
            read: false
        };

        // Add message to conversation
        await setDoc(doc(db, `directMessages/${conversationId}/messages`, messageId), message);

        // Update conversation metadata
        await updateDoc(doc(db, 'directMessages', conversationId), {
            lastMessage: content.substring(0, 100),
            lastMessageAt: Date.now()
        });

        return { success: true, message };
    } catch (error) {
        console.error('[SocialService] Send direct message error:', error);
        return { success: false };
    }
}

/**
 * Get messages for a conversation
 */
export async function getConversationMessages(
    conversationId: string,
    limitCount: number = 50
): Promise<DirectMessage[]> {
    try {
        const messagesQuery = query(
            collection(db, `directMessages/${conversationId}/messages`),
            orderBy('timestamp', 'desc'),
            limit(limitCount)
        );

        const snapshot = await getDocs(messagesQuery);
        const messages: DirectMessage[] = [];

        snapshot.forEach(doc => {
            messages.push(doc.data() as DirectMessage);
        });

        return messages.reverse(); // Return in chronological order
    } catch (error) {
        console.error('[SocialService] Get messages error:', error);
        return [];
    }
}

/**
 * Listen to conversation messages in real-time
 */
export function onConversationMessages(
    conversationId: string,
    callback: (messages: DirectMessage[]) => void
): () => void {
    const messagesQuery = query(
        collection(db, `directMessages/${conversationId}/messages`),
        orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(
        messagesQuery,
        (snapshot) => {
            const messages: DirectMessage[] = [];
            snapshot.forEach(doc => {
                messages.push(doc.data() as DirectMessage);
            });
            callback(messages);
        },
        (error) => {
            console.error('[SocialService] Messages listener error:', error);
        }
    );

    return unsubscribe;
}

/**
 * Mark messages as read
 */
export async function markMessagesAsRead(conversationId: string, userId: string): Promise<void> {
    try {
        const messagesQuery = query(
            collection(db, `directMessages/${conversationId}/messages`),
            where('receiverId', '==', userId),
            where('read', '==', false)
        );

        const snapshot = await getDocs(messagesQuery);
        const batch = writeBatch(db);

        snapshot.forEach(docSnap => {
            batch.update(doc(db, `directMessages/${conversationId}/messages`, docSnap.id), {
                read: true
            });
        });

        await batch.commit();
    } catch (error) {
        console.error('[SocialService] Mark as read error:', error);
    }
}

/**
 * Get user's conversations with friends
 */
export async function getUserConversations(userId: string, friends: Friend[]): Promise<Conversation[]> {
    try {
        const conversations: Conversation[] = [];

        for (const friend of friends) {
            const sortedIds = [userId, friend.id].sort();
            const conversationId = `dm_${sortedIds[0]}_${sortedIds[1]}`;

            const convDoc = await getDoc(doc(db, 'directMessages', conversationId));

            if (convDoc.exists()) {
                const data = convDoc.data();

                // Count unread messages
                const unreadQuery = query(
                    collection(db, `directMessages/${conversationId}/messages`),
                    where('receiverId', '==', userId),
                    where('read', '==', false)
                );
                const unreadSnap = await getDocs(unreadQuery);

                conversations.push({
                    id: conversationId,
                    participantIds: data.participantIds,
                    lastMessage: data.lastMessage || '',
                    lastMessageAt: data.lastMessageAt || 0,
                    unreadCount: unreadSnap.size,
                    friend
                });
            }
        }

        return conversations.sort((a, b) => b.lastMessageAt - a.lastMessageAt);
    } catch (error) {
        console.error('[SocialService] Get conversations error:', error);
        return [];
    }
}

// ==================== SUGGESTED FRIENDS ====================

/**
 * Get suggested friends for a user
 * Based on: similar level, active users, not already friends
 */
export async function getSuggestedFriends(userId: string, limitCount: number = 10): Promise<UserSearchResult[]> {
    try {
        // Get current user data
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (!userDoc.exists()) return [];

        const userData = userDoc.data();
        const userLevel = userData.level || 1;

        // Get user's existing friends
        const friendsSnapshot = await getDocs(collection(db, `users/${userId}/friends`));
        const friendIds = new Set(friendsSnapshot.docs.map(d => d.id));
        friendIds.add(userId); // Don't suggest self

        // Get pending requests sent by user
        const sentRequestsQuery = query(
            collection(db, 'friendRequests'),
            where('fromUserId', '==', userId),
            where('status', '==', 'pending')
        );
        const sentRequests = await getDocs(sentRequestsQuery);
        sentRequests.forEach(doc => {
            friendIds.add(doc.data().toUserId);
        });

        // Query active users (ordered by recent activity)
        const usersQuery = query(
            collection(db, 'users'),
            orderBy('lastActive', 'desc'),
            limit(100)
        );

        const usersSnapshot = await getDocs(usersQuery);
        const suggestions: UserSearchResult[] = [];

        usersSnapshot.forEach(doc => {
            if (!friendIds.has(doc.id) && suggestions.length < limitCount) {
                const data = doc.data();

                // Skip anonymous users
                if (data.isAnonymous) return;

                // Skip users without usernames
                if (!data.username) return;

                suggestions.push({
                    id: doc.id,
                    username: data.username,
                    level: data.level || 1,
                    rank: data.rank || 'Beginner',
                    tier: data.accountTier || 'free',
                    questsCompleted: data.questsCompleted || 0
                });
            }
        });

        // Sort by level similarity
        return suggestions.sort((a, b) => {
            const aDiff = Math.abs(a.level - userLevel);
            const bDiff = Math.abs(b.level - userLevel);
            return aDiff - bDiff;
        });
    } catch (error) {
        console.error('[SocialService] Get suggested friends error:', error);
        return [];
    }
}

// ==================== ACTIVITY FEED ====================

export interface Activity {
    id: string;
    userId: string;
    username: string;
    userTier: 'free' | 'premium' | 'max';
    type: 'master_subject' | 'master_topic' | 'achievement_unlocked' | 'level_up' | 'quest_complete';
    title: string;
    description: string;
    metadata?: any;
    timestamp: number;
    likes: string[]; // User IDs
}

/**
 * Publish a new activity
 */
export async function publishActivity(
    userId: string,
    username: string,
    userTier: 'free' | 'premium' | 'max',
    type: Activity['type'],
    title: string,
    description: string,
    metadata?: any
): Promise<void> {
    try {
        const activity: Activity = {
            id: `act_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            userId,
            username,
            userTier,
            type,
            title,
            description,
            metadata,
            timestamp: Date.now(),
            likes: []
        };

        await setDoc(doc(db, 'activities', activity.id), activity);
    } catch (error) {
        console.error('[SocialService] Publish activity error:', error);
    }
}

/**
 * Get recent activities (Global or Friends Only)
 */
export async function getRecentActivities(limitCount: number = 20, friendIds?: string[]): Promise<Activity[]> {
    try {
        let q;
        if (friendIds && friendIds.length > 0) {
            // Friends-only feed - batch in groups of 10 (Firebase limit)
            const allActivities: Activity[] = [];
            const batches: string[][] = [];
            for (let i = 0; i < friendIds.length; i += 10) {
                batches.push(friendIds.slice(i, i + 10));
            }
            for (const batch of batches) {
                const batchQuery = query(
                    collection(db, 'activities'),
                    where('userId', 'in', batch),
                    orderBy('timestamp', 'desc'),
                    limit(limitCount)
                );
                const snapshot = await getDocs(batchQuery);
                snapshot.forEach(doc => allActivities.push(doc.data() as Activity));
            }
            return allActivities.sort((a, b) => b.timestamp - a.timestamp).slice(0, limitCount);
        } else {
            q = query(
                collection(db, 'activities'),
                orderBy('timestamp', 'desc'),
                limit(limitCount)
            );
            const snapshot = await getDocs(q);
            const activities: Activity[] = [];
            snapshot.forEach(doc => activities.push(doc.data() as Activity));
            return activities;
        }
    } catch (error) {
        console.error('[SocialService] Get activities error:', error);
        return [];
    }
}

// ==================== ACTIVITY REACTIONS ====================

/**
 * Toggle a like/reaction on an activity
 */
export async function toggleActivityReaction(
    activityId: string,
    userId: string,
    reaction: string = 'like'
): Promise<{ liked: boolean }> {
    try {
        const activityRef = doc(db, 'activities', activityId);
        const activityDoc = await getDoc(activityRef);

        if (!activityDoc.exists()) return { liked: false };

        const data = activityDoc.data();
        const likes: string[] = data.likes || [];
        const reactionKey = `${userId}_${reaction}`;

        if (likes.includes(reactionKey) || likes.includes(userId)) {
            // Remove reaction
            await updateDoc(activityRef, {
                likes: likes.filter(l => l !== reactionKey && l !== userId)
            });
            return { liked: false };
        } else {
            // Add reaction
            await updateDoc(activityRef, {
                likes: [...likes, reaction === 'like' ? userId : reactionKey]
            });
            return { liked: true };
        }
    } catch (error) {
        console.error('[SocialService] Toggle reaction error:', error);
        return { liked: false };
    }
}

// ==================== ACTIVITY COMMENTS ====================

export interface ActivityComment {
    id: string;
    activityId: string;
    userId: string;
    username: string;
    userTier: 'free' | 'premium' | 'max';
    content: string;
    timestamp: number;
}

/**
 * Add a comment to an activity
 */
export async function addActivityComment(
    activityId: string,
    userId: string,
    username: string,
    userTier: 'free' | 'premium' | 'max',
    content: string
): Promise<ActivityComment | null> {
    try {
        const comment: ActivityComment = {
            id: `cmt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            activityId,
            userId,
            username,
            userTier,
            content: content.trim().substring(0, 500),
            timestamp: Date.now(),
        };

        await setDoc(
            doc(db, `activities/${activityId}/comments`, comment.id),
            comment
        );
        return comment;
    } catch (error) {
        console.error('[SocialService] Add comment error:', error);
        return null;
    }
}

/**
 * Get comments for an activity
 */
export async function getActivityComments(
    activityId: string,
    limitCount: number = 20
): Promise<ActivityComment[]> {
    try {
        const q = query(
            collection(db, `activities/${activityId}/comments`),
            orderBy('timestamp', 'asc'),
            limit(limitCount)
        );
        const snapshot = await getDocs(q);
        const comments: ActivityComment[] = [];
        snapshot.forEach(doc => comments.push(doc.data() as ActivityComment));
        return comments;
    } catch (error) {
        console.error('[SocialService] Get comments error:', error);
        return [];
    }
}

// ==================== STUDY CHALLENGES ====================

export interface StudyChallenge {
    id: string;
    creatorId: string;
    creatorUsername: string;
    creatorTier: 'free' | 'premium' | 'max';
    title: string;
    description: string;
    type: 'xp_race' | 'quiz_battle' | 'streak_challenge' | 'topic_mastery' | 'study_hours';
    goal: number; // Target value (XP, questions, days, hours)
    unit: string; // 'XP' | 'questions' | 'days' | 'hours'
    duration: number; // Duration in hours
    startTime: number;
    endTime: number;
    status: 'pending' | 'active' | 'completed' | 'cancelled';
    participants: ChallengeParticipant[];
    maxParticipants: number;
    inviteOnly: boolean;
    inviteCode?: string;
    reward?: string; // Description of bragging rights / badge
    createdAt: number;
}

export interface ChallengeParticipant {
    userId: string;
    username: string;
    tier: 'free' | 'premium' | 'max';
    progress: number;
    joinedAt: number;
    lastUpdated: number;
    completed: boolean;
}

export type ChallengeType = StudyChallenge['type'];

export const CHALLENGE_TYPES: { id: ChallengeType; label: string; icon: string; desc: string; unit: string; defaultGoal: number }[] = [
    { id: 'xp_race', label: 'XP Race', icon: '⚡', desc: 'Race to earn the most XP', unit: 'XP', defaultGoal: 500 },
    { id: 'quiz_battle', label: 'Quiz Battle', icon: '🧠', desc: 'Answer the most quiz questions correctly', unit: 'questions', defaultGoal: 50 },
    { id: 'streak_challenge', label: 'Streak Challenge', icon: '🔥', desc: 'Maintain a daily study streak', unit: 'days', defaultGoal: 7 },
    { id: 'topic_mastery', label: 'Topic Mastery', icon: '🎯', desc: 'Master a specific topic together', unit: 'topics', defaultGoal: 5 },
    { id: 'study_hours', label: 'Study Marathon', icon: '📚', desc: 'Log the most study hours', unit: 'hours', defaultGoal: 10 },
];

/**
 * Create a new study challenge
 */
export async function createStudyChallenge(
    creatorId: string,
    creatorUsername: string,
    creatorTier: 'free' | 'premium' | 'max',
    config: {
        title: string;
        description: string;
        type: ChallengeType;
        goal: number;
        durationHours: number;
        maxParticipants?: number;
        inviteOnly?: boolean;
        reward?: string;
    }
): Promise<StudyChallenge | null> {
    try {
        const now = Date.now();
        const challengeId = `ch_${now}_${Math.random().toString(36).substr(2, 9)}`;
        const inviteCode = Math.random().toString(36).substr(2, 6).toUpperCase();
        const typeInfo = CHALLENGE_TYPES.find(t => t.id === config.type);

        const challenge: StudyChallenge = {
            id: challengeId,
            creatorId,
            creatorUsername,
            creatorTier,
            title: config.title.trim().substring(0, 100),
            description: config.description.trim().substring(0, 300),
            type: config.type,
            goal: config.goal,
            unit: typeInfo?.unit || 'points',
            duration: config.durationHours,
            startTime: now,
            endTime: now + (config.durationHours * 60 * 60 * 1000),
            status: 'active',
            participants: [{
                userId: creatorId,
                username: creatorUsername,
                tier: creatorTier,
                progress: 0,
                joinedAt: now,
                lastUpdated: now,
                completed: false,
            }],
            maxParticipants: config.maxParticipants || 10,
            inviteOnly: config.inviteOnly || false,
            inviteCode,
            reward: config.reward,
            createdAt: now,
        };

        await setDoc(doc(db, 'studyChallenges', challengeId), challenge);
        console.log('[SocialService] Challenge created:', challengeId);
        return challenge;
    } catch (error) {
        console.error('[SocialService] Create challenge error:', error);
        return null;
    }
}

/**
 * Join a study challenge
 */
export async function joinStudyChallenge(
    challengeId: string,
    userId: string,
    username: string,
    userTier: 'free' | 'premium' | 'max'
): Promise<{ success: boolean; error?: string }> {
    try {
        const challengeRef = doc(db, 'studyChallenges', challengeId);
        const challengeDoc = await getDoc(challengeRef);

        if (!challengeDoc.exists()) return { success: false, error: 'Challenge not found' };

        const challenge = challengeDoc.data() as StudyChallenge;

        if (challenge.status !== 'active') return { success: false, error: 'Challenge is no longer active' };
        if (challenge.participants.some(p => p.userId === userId)) return { success: false, error: 'Already joined' };
        if (challenge.participants.length >= challenge.maxParticipants) return { success: false, error: 'Challenge is full' };
        if (Date.now() > challenge.endTime) return { success: false, error: 'Challenge has ended' };

        const newParticipant: ChallengeParticipant = {
            userId,
            username,
            tier: userTier,
            progress: 0,
            joinedAt: Date.now(),
            lastUpdated: Date.now(),
            completed: false,
        };

        await updateDoc(challengeRef, {
            participants: [...challenge.participants, newParticipant]
        });

        return { success: true };
    } catch (error) {
        console.error('[SocialService] Join challenge error:', error);
        return { success: false, error: 'Failed to join challenge' };
    }
}

/**
 * Update challenge progress for a participant
 */
export async function updateChallengeProgress(
    challengeId: string,
    userId: string,
    progressIncrement: number
): Promise<void> {
    try {
        const challengeRef = doc(db, 'studyChallenges', challengeId);
        const challengeDoc = await getDoc(challengeRef);

        if (!challengeDoc.exists()) return;

        const challenge = challengeDoc.data() as StudyChallenge;
        if (challenge.status !== 'active' || Date.now() > challenge.endTime) return;

        const updatedParticipants = challenge.participants.map(p => {
            if (p.userId === userId) {
                const newProgress = p.progress + progressIncrement;
                return {
                    ...p,
                    progress: newProgress,
                    lastUpdated: Date.now(),
                    completed: newProgress >= challenge.goal,
                };
            }
            return p;
        });

        await updateDoc(challengeRef, { participants: updatedParticipants });
    } catch (error) {
        console.error('[SocialService] Update progress error:', error);
    }
}

/**
 * Get active challenges (public or user's)
 */
export async function getActiveChallenges(
    userId?: string,
    limitCount: number = 20
): Promise<StudyChallenge[]> {
    try {
        const q = query(
            collection(db, 'studyChallenges'),
            where('status', '==', 'active'),
            orderBy('createdAt', 'desc'),
            limit(limitCount)
        );

        const snapshot = await getDocs(q);
        const challenges: StudyChallenge[] = [];
        snapshot.forEach(doc => {
            const data = doc.data() as StudyChallenge;
            // Include public challenges or ones the user is in
            if (!data.inviteOnly || (userId && data.participants.some(p => p.userId === userId))) {
                challenges.push(data);
            }
        });

        // Filter out expired
        const now = Date.now();
        return challenges.filter(c => c.endTime > now);
    } catch (error) {
        console.error('[SocialService] Get challenges error:', error);
        return [];
    }
}

/**
 * Get challenges the user is participating in
 */
export async function getUserChallenges(userId: string): Promise<StudyChallenge[]> {
    try {
        // Get all active challenges and filter by participant
        const q = query(
            collection(db, 'studyChallenges'),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        const snapshot = await getDocs(q);
        const challenges: StudyChallenge[] = [];
        snapshot.forEach(doc => {
            const data = doc.data() as StudyChallenge;
            if (data.participants.some(p => p.userId === userId)) {
                challenges.push(data);
            }
        });

        return challenges;
    } catch (error) {
        console.error('[SocialService] Get user challenges error:', error);
        return [];
    }
}

/**
 * Join challenge by invite code
 */
export async function joinChallengeByCode(
    inviteCode: string,
    userId: string,
    username: string,
    userTier: 'free' | 'premium' | 'max'
): Promise<{ success: boolean; challenge?: StudyChallenge; error?: string }> {
    try {
        const q = query(
            collection(db, 'studyChallenges'),
            where('inviteCode', '==', inviteCode.toUpperCase()),
            where('status', '==', 'active'),
            limit(1)
        );

        const snapshot = await getDocs(q);
        if (snapshot.empty) return { success: false, error: 'Invalid invite code' };

        const challengeData = snapshot.docs[0].data() as StudyChallenge;
        const result = await joinStudyChallenge(challengeData.id, userId, username, userTier);

        if (result.success) {
            return { success: true, challenge: challengeData };
        }
        return { success: false, error: result.error };
    } catch (error) {
        console.error('[SocialService] Join by code error:', error);
        return { success: false, error: 'Failed to join' };
    }
}

// ==================== USER PROFILE STATS ====================

export interface UserProfileStats {
    userId: string;
    username: string;
    tier: 'free' | 'premium' | 'max';
    level: number;
    rank: string;
    totalXP: number;
    weeklyXP: number;
    streak: number;
    longestStreak: number;
    questsCompleted: number;
    friendCount: number;
    challengesWon: number;
    challengesJoined: number;
    studyHoursThisWeek: number;
    topSubjects: string[];
    joinedAt: number;
    lastActive: number;
    bio?: string;
    badges: string[];
}

/**
 * Get detailed profile stats for a user
 */
export async function getUserProfileStats(userId: string): Promise<UserProfileStats | null> {
    try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (!userDoc.exists()) return null;

        const data = userDoc.data();

        // Count friends
        const friendsSnapshot = await getDocs(collection(db, `users/${userId}/friends`));
        const friendCount = friendsSnapshot.size;

        // Count challenges
        let challengesWon = 0;
        let challengesJoined = 0;
        try {
            const challengesQuery = query(
                collection(db, 'studyChallenges'),
                limit(100)
            );
            const challengesSnapshot = await getDocs(challengesQuery);
            challengesSnapshot.forEach(doc => {
                const ch = doc.data() as StudyChallenge;
                const participant = ch.participants.find(p => p.userId === userId);
                if (participant) {
                    challengesJoined++;
                    if (participant.completed) challengesWon++;
                }
            });
        } catch (e) {
            // Challenges collection might not exist yet
        }

        let tier: 'free' | 'premium' | 'max' = 'free';
        if (data.accountTier === 'max' || data.isMax) tier = 'max';
        else if (data.accountTier === 'premium' || data.isPremium) tier = 'premium';

        return {
            userId,
            username: data.username || 'Anonymous',
            tier,
            level: data.level || 1,
            rank: data.rank || 'Novice',
            totalXP: data.totalXP || 0,
            weeklyXP: data.weeklyXP || 0,
            streak: data.streakCount || data.currentStreak || 0,
            longestStreak: data.longestStreak || data.streakCount || 0,
            questsCompleted: data.questsCompleted || 0,
            friendCount,
            challengesWon,
            challengesJoined,
            studyHoursThisWeek: data.studyHoursThisWeek || 0,
            topSubjects: data.topSubjects || [],
            joinedAt: data.createdAt || data.joinedAt || Date.now(),
            lastActive: data.lastActive || Date.now(),
            bio: data.bio || '',
            badges: data.badges || [],
        };
    } catch (error) {
        console.error('[SocialService] Get profile stats error:', error);
        return null;
    }
}

/**
 * Update user bio
 */
export async function updateUserBio(userId: string, bio: string): Promise<void> {
    try {
        await updateDoc(doc(db, 'users', userId), {
            bio: bio.trim().substring(0, 200)
        });
    } catch (error) {
        console.error('[SocialService] Update bio error:', error);
    }
}

// ==================== STUDY STATS SHARING ====================

/**
 * Share study stats as an activity post
 */
// ==================== EXAM SCORE LEADERBOARD ====================

export interface ExamLeaderboardEntry {
    userId: string;
    username: string;
    level: number;
    rank: string;
    tier: 'free' | 'premium' | 'max';
    totalScore: number;
    subjectScores: Record<string, number>; // e.g. { FAR: 85, AFAR: 72, ... }
    quizSubjectScores: Record<string, number>; // quiz-only scores per subject
    examSubjectScores: Record<string, number>; // mock-exam-only scores per subject
    quizTotalScore: number;
    examTotalScore: number;
    quizCount: number;
    examCount: number;
}

// Map quiz topic / mock exam subject strings to CPALE subject codes
const CPALE_SUBJECT_MAP: Record<string, string> = {
    // Quiz categories
    'financial accounting & reporting': 'FAR',
    'advanced financial accounting': 'AFAR',
    'management advisory services': 'MAS',
    'auditing': 'AUD',
    'taxation': 'TAX',
    'regulatory framework for business transactions': 'RFBT',
    // Mock exam short names
    'far': 'FAR',
    'afar': 'AFAR',
    'mas': 'MAS',
    'aud': 'AUD',
    'tax': 'TAX',
    'rfbt': 'RFBT',
    // Mock exam full names (lowercase)
    'financial accounting and reporting': 'FAR',
};

function mapToSubjectCode(raw: string): string | null {
    const key = raw.trim().toLowerCase();
    if (CPALE_SUBJECT_MAP[key]) return CPALE_SUBJECT_MAP[key];
    // Partial match fallback
    for (const [pattern, code] of Object.entries(CPALE_SUBJECT_MAP)) {
        if (key.includes(pattern) || pattern.includes(key)) return code;
    }
    return null;
}

/**
 * Get Exam Score Leaderboard
 * Aggregates total scores from quizHistory + mockExamHistory across all users,
 * broken down by CPALE subjects: FAR, AFAR, MAS, TAX, AUD, RFBT.
 */
export async function getExamLeaderboard(limitCount: number = 20): Promise<ExamLeaderboardEntry[]> {
    try {
        // Fetch users ordered by totalXP as a rough proxy; we re-sort by total score after
        const usersQuery = query(
            collection(db, 'users'),
            orderBy('totalXP', 'desc'),
            limit(100) // fetch a reasonable pool
        );

        const snapshot = await getDocs(usersQuery);
        const entries: ExamLeaderboardEntry[] = [];

        snapshot.forEach(docSnap => {
            const data = docSnap.data();
            if (data.isAnonymous || !data.username) return;

            const quizSubjectScores: Record<string, number> = {
                FAR: 0, AFAR: 0, MAS: 0, TAX: 0, AUD: 0, RFBT: 0
            };
            const examSubjectScores: Record<string, number> = {
                FAR: 0, AFAR: 0, MAS: 0, TAX: 0, AUD: 0, RFBT: 0
            };
            let quizCount = 0;
            let examCount = 0;

            // Aggregate quiz scores
            const quizHistory: any[] = data.quizHistory || [];
            quizHistory.forEach((q: any) => {
                const topic = q.topic || '';
                const code = mapToSubjectCode(topic);
                if (code && quizSubjectScores[code] !== undefined) {
                    quizSubjectScores[code] += (q.score || 0);
                    quizCount++;
                }
            });

            // Aggregate mock exam scores
            const mockExamHistory: any[] = data.mockExamHistory || [];
            mockExamHistory.forEach((m: any) => {
                if (m.sections && Array.isArray(m.sections)) {
                    m.sections.forEach((sec: any) => {
                        const code = mapToSubjectCode(sec.name || '');
                        if (code && examSubjectScores[code] !== undefined) {
                            examSubjectScores[code] += (sec.score || 0);
                        }
                    });
                    examCount++;
                } else {
                    const subj = m.subject || '';
                    const code = mapToSubjectCode(subj);
                    if (code && examSubjectScores[code] !== undefined) {
                        examSubjectScores[code] += (m.score || 0);
                    }
                    examCount++;
                }
            });

            // Combined subject scores
            const subjectScores: Record<string, number> = { FAR: 0, AFAR: 0, MAS: 0, TAX: 0, AUD: 0, RFBT: 0 };
            for (const key of Object.keys(subjectScores)) {
                subjectScores[key] = quizSubjectScores[key] + examSubjectScores[key];
            }

            const quizTotalScore = Object.values(quizSubjectScores).reduce((a, b) => a + b, 0);
            const examTotalScore = Object.values(examSubjectScores).reduce((a, b) => a + b, 0);
            const totalScore = quizTotalScore + examTotalScore;

            if (totalScore > 0 || quizCount > 0 || examCount > 0) {
                entries.push({
                    userId: docSnap.id,
                    username: data.username,
                    level: data.level || 1,
                    rank: data.rank || 'Beginner',
                    tier: data.accountTier || 'free',
                    totalScore,
                    subjectScores,
                    quizSubjectScores,
                    examSubjectScores,
                    quizTotalScore,
                    examTotalScore,
                    quizCount,
                    examCount,
                });
            }
        });

        // Sort by total score descending
        entries.sort((a, b) => b.totalScore - a.totalScore);
        return entries.slice(0, limitCount);
    } catch (error) {
        console.error('[SocialService] Get exam leaderboard error:', error);
        return [];
    }
}

export async function shareStudyStats(
    userId: string,
    username: string,
    userTier: 'free' | 'premium' | 'max',
    stats: { label: string; value: string | number }[],
    message?: string
): Promise<void> {
    const statsText = stats.map(s => `${s.label}: ${s.value}`).join(' • ');
    await publishActivity(
        userId,
        username,
        userTier,
        'achievement_unlocked',
        message || '📊 Shared study stats',
        statsText,
        { type: 'stats_share', stats }
    );
}

/**
 * Share a challenge completion as an activity
 */
export async function shareChallengeCompletion(
    userId: string,
    username: string,
    userTier: 'free' | 'premium' | 'max',
    challenge: StudyChallenge,
    rank: number
): Promise<void> {
    const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : '🏅';
    await publishActivity(
        userId,
        username,
        userTier,
        'quest_complete',
        `${medal} Finished #${rank} in "${challenge.title}"`,
        `${challenge.type.replace('_', ' ')} challenge — ${challenge.goal} ${challenge.unit} goal`,
        { type: 'challenge_complete', challengeId: challenge.id, rank }
    );
}